/**
 * The app, translating, on the home page.
 *
 * Structure and values are taken from the real client — MainPanel's advanced
 * control footer, ConversationRow, WaveformStrip — but this is a purpose-written
 * reproduction rather than a copy of the app's stylesheet. Shipping 23 kB of
 * client CSS into the marketing bundle would be dead weight that drifts from the
 * app the moment either side changes; the values that carry meaning are pinned
 * here with a comment saying where they came from.
 *
 * One 24-second pass shows two exchanges: you speak and the microphone strip
 * moves, the source line streams in, the translation follows, it is spoken so
 * the karaoke sweep and the output strip run together, then the other side
 * answers. All of it is CSS — no timers, nothing to clean up, and it stops
 * entirely under prefers-reduced-motion.
 */

import { useId } from 'react';
import {
  AArrowDown,
  AArrowUp,
  ArrowLeftRight,
  Captions,
  ChevronDown,
  ChevronsDownUp,
  Download,
  Lock,
  Maximize,
  Pin,
  Play,
  Settings,
  Square,
  Trash2,
  User,
  Users,
  X,
} from 'lucide-react';
import { useI18n } from '@/lib/i18n';
import {
  DEMO_SCRIPT,
  LANGUAGE_TAG,
  counterpartLocale,
  revealSteps,
} from './demoScript';
import './AppDemo.scss';

/** Ten bars per strip: WavRenderer.drawBars is called with pointCount 10. */
const BARS = Array.from({ length: 10 }, (_, i) => i);

/**
 * The call the subtitle bar floats on: the two who talk, in the top row where
 * their speaking rings stay clear of the bar, and two who listen, under it.
 */
const SCENE_TILES = ['me', 'them', 'a', 'b'] as const;

/**
 * One channel's level meter. The app labels these for assistive tech only —
 * the colour is what tells them apart on screen — so the name is carried but
 * not drawn.
 */
function Strip({ channel, label }: { channel: 'mic' | 'sys' | 'out'; label: string }) {
  return (
    <span className={`app-demo__strip app-demo__strip--${channel}`}>
      <span className="app-demo__sr">{label}</span>
      {BARS.map((i) => (
        <span key={i} className="app-demo__bar" style={{ '--i': i } as React.CSSProperties}>
          <span className="app-demo__amp" />
        </span>
      ))}
    </span>
  );
}

/**
 * One side's display-mode control: a radio group of the three states, and
 * three labels, each showing while its state is current and pointing at the
 * radio for the *next* one. None is checked until someone clicks. `.is-cyc`
 * is hidden by default, so the panel's toolbar starts empty and fills in
 * once a choice has been made — which is what the app does, and what makes
 * this a control rather than a caption.
 *
 * The subtitle bar carries the same control with its own state: the app keeps
 * the subtitle store's display modes apart from the panel's, and starts them
 * at Both. The stylesheet shows that side's Both until a choice is made.
 */
function Cycler({
  side,
  name,
  who,
  labels,
  scope = 'panel',
}: {
  side: 'sp' | 'pt';
  name: string;
  /** Whose display this sets, for the radios' names. */
  who: string;
  labels: { both: string; src: string; trans: string };
  scope?: 'panel' | 'bar';
}) {
  const Icon = side === 'sp' ? User : Users;
  const key = scope === 'bar' ? `bar-${side}` : side;
  // state  ->  label shown while it is current  ->  radio that label selects
  const states: [string, keyof typeof labels, string][] = [
    ['both', 'both', 'source'],
    ['source', 'src', 'translation'],
    ['translation', 'trans', 'both'],
  ];

  return (
    <>
      {/* The radios sit beside the labels, not inside them. A radio inside a
          label the stylesheet hides is unfocusable, and a group whose checked
          member is unfocusable is one the keyboard cannot enter — so the first
          choice a keyboard made was the last it could make. Out here the
          checked radio is always a tab stop and the arrow keys cycle the
          group; the label that is showing is where its focus ring is drawn. */}
      {states.map(([state, shown]) => (
        <input
          key={state}
          type="radio"
          name={name}
          id={`${name}-${state}`}
          className={`in-${key}-${state}`}
          aria-label={`${who}: ${labels[shown]}`}
        />
      ))}
      {states.map(([, shown, selects]) => (
        <label
          key={shown}
          className={`app-demo__disp is-cyc ${key}-${shown}`}
          htmlFor={`${name}-${selects}`}
        >
          <Icon size={scope === 'bar' ? 13 : 11} aria-hidden="true" />
          {labels[shown]}
        </label>
      ))}
    </>
  );
}

/**
 * One turn on a subtitle line.
 *
 * Subtitle mode does not scroll a transcript: it holds four regions — each
 * side's original and each side's translation — and appends to them. So a
 * region carries both of that side's turns, and `slot` is what gates when the
 * second one arrives.
 */
function SubItem({
  slot,
  text,
  spoken,
}: {
  slot: 'b1' | 'b2' | 'b3' | 'b4';
  text: string;
  spoken?: boolean;
}) {
  return (
    <span className={`app-demo__sub-item ${slot}`}>
      <span
        className="app-demo__sub-text"
        style={{ '--steps': revealSteps(text) } as React.CSSProperties}
      >
        {spoken ? (
          <span className="app-demo__karaoke">
            {text}
            <span className="app-demo__karaoke-fill" aria-hidden="true">
              {text}
            </span>
          </span>
        ) : (
          text
        )}
      </span>
    </span>
  );
}

interface RowProps {
  slot: 'r1' | 'r2' | 'r3' | 'r4';
  who: 'speaker' | 'participant';
  name: string;
  time: string;
  sourceTag: string;
  source: string;
  targetTag: string;
  target: string;
  /** Only a speaker translation is spoken, so only it sweeps. */
  spoken?: boolean;
}

function Row({ slot, who, name, time, sourceTag, source, targetTag, target, spoken }: RowProps) {
  return (
    <div className={`app-demo__row app-demo__row--${who} ${slot}`}>
      <div className="app-demo__row-head">
        <span className={`app-demo__avatar app-demo__avatar--${who}`}>
          {who === 'speaker' ? <User size={12} /> : <Users size={12} />}
        </span>
        <span className="app-demo__who">{name}</span>
        <span className="app-demo__time">{time}</span>
      </div>

      <div className="app-demo__line app-demo__line--source">
        <span className="app-demo__tag">{sourceTag}</span>
        <span
          className="app-demo__text app-demo__text--source"
          style={{ '--steps': revealSteps(source) } as React.CSSProperties}
        >
          {source}
        </span>
      </div>

      <div className={`app-demo__line app-demo__line--target ${spoken ? 'is-spoken' : ''}`}>
        <span className={`app-demo__tag app-demo__tag--${who}`}>{targetTag}</span>
        <span
          className="app-demo__text app-demo__text--target"
          style={{ '--steps': revealSteps(target) } as React.CSSProperties}
        >
          {spoken ? (
            <span className="app-demo__karaoke">
              {target}
              <span className="app-demo__karaoke-fill" aria-hidden="true">
                {target}
              </span>
            </span>
          ) : (
            target
          )}
        </span>
        {spoken && (
          <span className="app-demo__play" aria-hidden="true">
            <Play size={9} fill="currentColor" />
          </span>
        )}
      </div>
    </div>
  );
}

export function AppDemo() {
  const { locale, t } = useI18n();
  const other = counterpartLocale(locale);
  const mine = DEMO_SCRIPT[locale];
  const theirs = DEMO_SCRIPT[other];
  const myTag = LANGUAGE_TAG[locale];
  const theirTag = LANGUAGE_TAG[other];

  const me = t('demo.me');
  const them = t('demo.other');
  const both = t('demo.both');
  const modes = { both, src: t('demo.src'), trans: t('demo.trans') };

  // The toggle is a real checkbox, so the panel switches with no JavaScript and
  // works from the keyboard. The id has to be unique on the page even if the
  // panel is ever rendered twice.
  const capId = useId();

  return (
    <div className="app-demo">
      <input type="checkbox" id={capId} className="app-demo__cap" />

      {/* What the bar floats on once the mode is entered. The app resizes its
          own window into the bar and unmounts everything else, so nothing of
          Sokuji is behind it: what shows through is whatever the user had on
          screen. A generic call stands in for that. It carries no text, so it
          has nothing to translate and nothing to say to a screen reader. */}
      <div className="app-demo__scene" aria-hidden="true">
        {SCENE_TILES.map((who) => (
          <span key={who} className={`app-demo__tile app-demo__tile--${who}`}>
            <span className="app-demo__tile-avatar">
              <User size={20} />
            </span>
          </span>
        ))}
      </div>

      <div className="app-demo__titlebar">
        <span className="app-demo__brand">Sokuji</span>
        <span className="app-demo__actions">
          {/* The one control that does something. The other two are part of the
              picture of the window and are hidden from assistive tech. */}
          <label className="app-demo__mode-btn app-demo__cap-btn" htmlFor={capId}>
            <Captions size={12} aria-hidden="true" />
            {t('demo.subtitle')}
          </label>
          <span className="app-demo__mode-btn" aria-hidden="true">
            <User size={12} />
            {t('common.signIn')}
          </span>
          <span className="app-demo__mode-btn" aria-hidden="true">
            <Settings size={12} />
            {t('demo.settings')}
          </span>
        </span>
      </div>

      <div className="app-demo__wrapper">
        {/* What each side is showing. Empty until you pick: the app starts with
            no choice made, and the labels appear as you cycle them. */}
        <div className="app-demo__toolbar">
          <Cycler side="sp" name={`${capId}-sp`} who={me} labels={modes} />
          <Cycler side="pt" name={`${capId}-pt`} who={them} labels={modes} />
        </div>

        {/* Newest first: the list is column-reverse so it rests at the newest end
            and still scrolls. `justify-content: flex-end` would look the same and
            then let content escape the top with no way to reach it. */}
        <div
          className="app-demo__conversation"
          // Described rather than hidden: the panel is the claim in the headline,
          // shown working. The description is built from the script itself, so it
          // is already in the right two languages and needs no separate string.
          role="img"
          aria-label={`${mine.ask} → ${theirs.ask}`}
        >
          <Row
            slot="r4"
            who="participant"
            name={them}
            time="10:26"
            sourceTag={theirTag}
            source={theirs.book}
            targetTag={myTag}
            target={mine.book}
          />
          <Row
            slot="r3"
            who="speaker"
            name={me}
            time="10:25"
            sourceTag={myTag}
            source={mine.plan}
            targetTag={theirTag}
            target={theirs.plan}
            spoken
          />
          <Row
            slot="r2"
            who="participant"
            name={them}
            time="10:25"
            sourceTag={theirTag}
            source={theirs.agree}
            targetTag={myTag}
            target={mine.agree}
          />
          <Row
            slot="r1"
            who="speaker"
            name={me}
            time="10:24"
            sourceTag={myTag}
            source={mine.ask}
            targetTag={theirTag}
            target={theirs.ask}
            spoken
          />
        </div>

        <div className="app-demo__footer">
          <span className="app-demo__status" />

          <span className="app-demo__picker">
            <span className="app-demo__seg" title={me}>
              <User size={12} />
            </span>
            <span className="app-demo__seg" title={them}>
              <Users size={12} />
            </span>
            <span className="app-demo__seg is-on" title={both}>
              <ArrowLeftRight size={12} />
            </span>
          </span>

          {/* Your microphone and the other side's audio are one group; what
              Sokuji speaks out sits apart, past the session control. */}
          <span className="app-demo__strips">
            <Strip channel="mic" label={t('demo.channel.mic')} />
            <Strip channel="sys" label={t('demo.channel.sys')} />
          </span>

          <span className="app-demo__spacer" />
          <span className="app-demo__end" title={t('demo.endSession')}>
            <X size={13} />
          </span>
          <span className="app-demo__spacer" />

          <Strip channel="out" label={t('demo.channel.out')} />

          <span className="app-demo__pair">{`${myTag} \u2192 ${theirTag}`}</span>
        </div>
      </div>

      {/* Subtitle mode. Not the panel with different styling: the app turns
          its window into this bar, with four fixed regions, and what arrives
          is appended to the region it belongs to rather than pushing a
          transcript along. */}
      <div className="app-demo__subtitle">
        {/* The app's own subtitle toolbar. Every control it carries is drawn,
            because the bar's density is part of what the picture is saying:
            this is a working window, not a caption strip. The two display
            modes and the exit are wired — the rest are the window's furniture. */}
        <div className="app-demo__sub-bar" role="toolbar" aria-label="Sokuji">
          <span className="app-demo__sub-group">
            <span className="app-demo__sub-stop" aria-hidden="true">
              <Square size={13} />
            </span>
            <span className="app-demo__brand app-demo__brand--live">Sokuji</span>
          </span>

          <span className="app-demo__sub-group">
            <span className="app-demo__sub-timer">00:00:00</span>
            <span className="app-demo__sub-lang">
              {`${myTag.toUpperCase()} \u2192 ${theirTag.toUpperCase()}`}
            </span>
          </span>

          <span className="app-demo__sub-group app-demo__sub-group--right">
            <Cycler scope="bar" side="sp" name={`${capId}-bar-sp`} who={me} labels={modes} />
            <Cycler scope="bar" side="pt" name={`${capId}-bar-pt`} who={them} labels={modes} />
            {/* Furniture: drawn, not wired, and not announced. `display:
                contents`, so the group's gap still runs between them. */}
            <span className="app-demo__sub-furniture" aria-hidden="true">
              <span className="app-demo__sub-btn"><AArrowDown size={13} /></span>
              <span className="app-demo__sub-btn"><AArrowUp size={13} /></span>
              <span className="app-demo__sub-btn"><ChevronsDownUp size={13} /></span>
              <span className="app-demo__sub-btn app-demo__sub-btn--export">
                <Download size={13} />
                <ChevronDown size={11} />
              </span>
              <span className="app-demo__sub-btn"><Trash2 size={13} /></span>
              <span className="app-demo__sub-divider" />
              <span className="app-demo__sub-btn"><Settings size={13} /></span>
              <span className="app-demo__sub-btn"><Maximize size={13} /></span>
              <span className="app-demo__sub-btn"><Pin size={13} /></span>
              <span className="app-demo__sub-btn"><Lock size={13} /></span>
            </span>
          {/* Inside the right-hand group, not a fourth child: the bar is three
              groups spaced apart, and a fourth would pull them out of place. */}
          <label
            className="app-demo__sub-btn app-demo__cap-btn app-demo__sub-exit"
            htmlFor={capId}
            title={t('demo.exitSubtitle')}
          >
            <X size={13} aria-hidden="true" />
            <span className="app-demo__sr">{t('demo.exitSubtitle')}</span>
          </label>
          </span>
        </div>

        <div
          className="app-demo__sub-stream"
          role="img"
          aria-label={`${mine.ask} → ${theirs.ask}`}
        >
          <div className="app-demo__sub-line app-demo__sub-line--source app-demo__sub-line--speaker">
            <p className="app-demo__sub-p">
              <SubItem slot="b1" text={mine.ask} />
              <SubItem slot="b3" text={mine.plan} />
            </p>
          </div>
          <div className="app-demo__sub-line app-demo__sub-line--target app-demo__sub-line--speaker">
            <p className="app-demo__sub-p">
              <SubItem slot="b1" text={theirs.ask} spoken />
              <SubItem slot="b3" text={theirs.plan} spoken />
            </p>
          </div>
          <div className="app-demo__sub-line app-demo__sub-line--source app-demo__sub-line--participant">
            <p className="app-demo__sub-p">
              <SubItem slot="b2" text={theirs.agree} />
              <SubItem slot="b4" text={theirs.book} />
            </p>
          </div>
          <div className="app-demo__sub-line app-demo__sub-line--target app-demo__sub-line--participant">
            <p className="app-demo__sub-p">
              <SubItem slot="b2" text={mine.agree} />
              <SubItem slot="b4" text={mine.book} />
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
